import { Instagram, Play } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";

type Props = {
  youtubeId: string | null;
  instagramUrl: string | null;
  title: string;
  posterUrl?: string | null;
};

export default function VideoEmbed({ youtubeId, instagramUrl, title, posterUrl }: Props) {
  const theme = usePalette();

  if (youtubeId) {
    return (
      <div className="relative">
        <div
          className="relative aspect-video w-full overflow-hidden rounded-3xl"
          style={{
            boxShadow: `0 25px 50px -25px ${theme.blob1}60`,
            border: `1px solid ${colors.tan}25`,
          }}
        >
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(youtubeId)}?rel=0&modestbranding=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute inset-0 h-full w-full"
            loading="lazy"
          />
        </div>
        {instagramUrl && (
          <a
            href={instagramUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-2 text-xs underline"
            style={{
              fontFamily: fonts.body,
              fontWeight: 700,
              color: theme.accent,
              letterSpacing: "0.05em",
            }}
          >
            <Instagram size={14} />
            Also on Instagram
          </a>
        )}
      </div>
    );
  }

  if (instagramUrl) {
    return (
      <a
        href={instagramUrl}
        target="_blank"
        rel="noreferrer"
        className="group block aspect-video w-full overflow-hidden rounded-3xl"
        style={{
          background: posterUrl
            ? `center/cover no-repeat url(${posterUrl})`
            : `linear-gradient(135deg, ${theme.blob1}, ${theme.warm}, ${colors.navy})`,
          border: `1px solid ${colors.tan}25`,
        }}
      >
        <div
          className="flex h-full w-full items-center justify-center text-white transition-colors"
          style={{ backgroundColor: "rgba(8,45,61,0.45)" }}
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full transition-transform group-hover:scale-110"
              style={{ backgroundColor: "rgba(255,255,255,0.92)", color: theme.accent }}
            >
              <Play size={28} fill="currentColor" />
            </div>
            <p
              className="text-xs uppercase"
              style={{ fontFamily: fonts.body, fontWeight: 700, letterSpacing: "0.18em" }}
            >
              Watch on Instagram
            </p>
          </div>
        </div>
      </a>
    );
  }

  return (
    <div
      className="flex aspect-video w-full items-center justify-center rounded-3xl"
      style={{
        backgroundColor: `${theme.accentSoft}`,
        border: `1px dashed ${colors.tan}55`,
        color: `${colors.navy}88`,
        fontFamily: fonts.body,
      }}
    >
      Video coming soon.
    </div>
  );
}
